import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import LegacyBetContext from "@root/bet_utils/context/bet"
import { IOneXTwoContext, OneXTwoContext } from '../OneXTwoBet/WithOneXTwoBet'
import { TOneXTwoBet } from '../OneXTwoBet/types'
import { createErrorResult, createOkResult } from '@root/bet_utils/DisplaySaveResult'
import useDeadlineCountdown from '@root/bet_utils/use_deadline_countdown'
import { PickType } from '@root/bet_utils/types'
import { RawOneXTwoBet } from '../OneXTwoBet'

const dateStringFromNow = (plusHours: number) => {
  return new Date(new Date().getTime() + plusHours * 60 * 60 * 1000).toISOString()
}

const BASE_BET: TOneXTwoBet = {
  bet_id: 'sandbox_123',
  bet_type: 'one_x_two',
  event_state: 'open',
  event_name: 'Quarterfinals',
  event_date: dateStringFromNow(0),
  event_over: false,
  deadline: dateStringFromNow(10),

  meta_location: 'Superstadium',

  from_other: false,
  censored: false,

  pick_mode: 'home_or_away_or_draw',
  teams: {
    home: {
      uid: 'sandbox_home',
      label: 'Germany',
      flag: 'https://flagsapi.com/DE/flat/64.png',
    },
    away: {
      uid: 'sandbox_away',
      label: 'Swiss',
      flag: 'https://flagsapi.com/CH/flat/64.png',
    }
  },

  total_score: 0,
}

const MockContext: React.FC<PropsWithChildren<{ initialBet: TOneXTwoBet }>> = ({ children, initialBet }) => {
  const [ bet, setBet ] = useState(initialBet)
  const [ displaySaveResult, setDisplaySaveResult ] = useState<ReturnType<typeof createOkResult>>()
  const secondsLeft = useDeadlineCountdown(bet.deadline)

  const changePicks = useCallback((newPicks: PickType<TOneXTwoBet>[]) => {
    setBet((prev) => ({ ...prev, picks: newPicks }))
    window.setTimeout(() => {
      setDisplaySaveResult(Math.random() > .5 ? createOkResult('Tipp gespeichert') : createErrorResult('Es ist ein Fehler aufgetreten (mocked message)'))
    }, 500);
  }, [])

  const context = useMemo<IOneXTwoContext>(() => ({
    bet: { ...bet, secondsLeft },
    changePicks,
    displaySaveResult,
  }), [ bet, displaySaveResult, secondsLeft ])

  return <LegacyBetContext.Provider value={context.bet}>
    <OneXTwoContext.Provider value={context}>
      {children}
    </OneXTwoContext.Provider>
  </LegacyBetContext.Provider>
}

const Section: React.FC<PropsWithChildren<{title: string, subtitle?: string, note?: React.ReactNode}>> = ({ title, children, subtitle, note }) => {
  return <div>
    <h2 className="sandbox__blockTitle" title={subtitle}>{title}</h2>
    {note &&
      <div className="sandbox__note">{note}</div>}
    <div className="layout__contentRow">
      {children}
    </div>
  </div>
} 

const OneXTwoBetSandbox: React.FC = () => {
  return <div>
    <Section title='own, open'>
      <MockContext initialBet={BASE_BET}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, open' subtitle='no draw (coundtdown counting seconds)'>
      <MockContext initialBet={{...BASE_BET, deadline: dateStringFromNow(2), pick_mode: 'home_or_away'}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, open' subtitle='teams TBD'>
      <MockContext initialBet={{...BASE_BET, pick_mode: 'home_or_away', teams: {}}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, closed' subtitle='in progress'>
      <MockContext initialBet={{...BASE_BET, event_state: 'progress', picks: ['home']}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, cancelled' subtitle={undefined}>
      <MockContext initialBet={{...BASE_BET, event_state: 'cancelled', cancelled: true}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, postponed' subtitle={undefined}>
      <MockContext initialBet={{...BASE_BET, event_state: 'cancelled', postponed: true}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, closed' subtitle='in progress, no pick'>
      <MockContext initialBet={{...BASE_BET, event_state: 'progress'}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, closed' subtitle='scored, correct'>
      <MockContext initialBet={{...BASE_BET, event_state: 'over', total_score: 3, actual_result: 'home', picks: ['home']}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, closed' subtitle='scored, wrong'>
      <MockContext initialBet={{...BASE_BET, event_state: 'over', total_score: 0, actual_result: 'draw', picks: ['home']}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>

    <Section title='own, closed' subtitle='scored, not picked'>
      <MockContext initialBet={{...BASE_BET, event_state: 'over', total_score: 0, actual_result: 'draw'}}>
        <RawOneXTwoBet />
      </MockContext>
    </Section>
  </div>
}

export default OneXTwoBetSandbox
